import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LANGUAGES, SUPPORTED_LOCALES } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from '../lib/api';
import { toast } from 'sonner';
import AuthDialog from '../components/AuthDialog';
import {
  Envelope, Lock, GoogleLogo, Sun, Moon, ArrowRight, List, CaretRight, CaretDown,
  Sparkle, ChartLineUp, Folder, Handshake, Shield, Lightning, ArrowLeft, Copy, CheckCircle,
  CurrencyDollar, ShoppingCart, TrendUp, Truck, UsersThree, Buildings, Scales, HeartStraight,
  MagnifyingGlass, X, Factory, Storefront, Heartbeat, Bank as BankIcon, Atom, Lightbulb,
  GraduationCap, ChatsCircle, Wrench, Headset, CalendarBlank,
} from '@phosphor-icons/react';

const PRODUCTS = [
  {
    key: 'finance',
    label: 'Finance',
    desc: 'Invoices, budgets, journals & real-time P&L.',
    icon: CurrencyDollar,
    tint: '#00B140',
  },
  {
    key: 'procurement',
    label: 'Procurement',
    desc: 'Source-to-pay, supplier scoring & RFQs.',
    icon: ShoppingCart,
    tint: '#6B7280',
  },
  {
    key: 'invest',
    label: 'Invest',
    desc: 'Marketplace to buy, sell & bid on businesses.',
    icon: TrendUp,
    tint: '#002677',
  },
  {
    key: 'supply-chain',
    label: 'Supply Chain',
    desc: 'End-to-end logistics, inventory & fulfilment.',
    icon: Truck,
    tint: '#8347AD',
  },
  {
    key: 'hr',
    label: 'Human Resources',
    desc: 'Employees, leave, performance & payroll.',
    icon: UsersThree,
    tint: '#E4002B',
  },
  {
    key: 'schedule',
    label: 'Schedule & Remind',
    desc: 'Lightweight events with browser reminders — free for everyone.',
    icon: CalendarBlank,
    tint: '#41B6E6',
    free: true,
  },
];

const SOLUTIONS = [
  {
    key: 'erp',
    label: 'ERP',
    desc: 'Unified Finance, HR & Operations on one data fabric.',
    icon: Buildings,
    tint: '#002677',
  },
  {
    key: 'grc',
    label: 'GRC',
    desc: 'Governance, risk, audit & compliance command center.',
    icon: Scales,
    tint: '#E4002B',
  },
  {
    key: 'procurement',
    label: 'Procurement',
    desc: 'Strategic sourcing, supplier risk & spend control.',
    icon: ShoppingCart,
    tint: '#6B7280',
  },
  {
    key: 'cx',
    label: 'Customer Experience',
    desc: 'CRM, service, marketing & loyalty in one copilot.',
    icon: HeartStraight,
    tint: '#8347AD',
  },
];

const NAV_ITEMS = [
  { key: 'overview',  label: 'Overview' },
  { key: 'products',  label: 'Products', hasDropdown: true },
  { key: 'solutions', label: 'Solutions', hasDropdown: true },
  { key: 'pricing',   label: 'Pricing' },
  { key: 'use-cases', label: 'Use cases' },
  { key: 'partners',  label: 'Partners' },
  { key: 'resources', label: 'Resources' },
  { key: 'support',   label: 'Support' },
];

// Slide-in hamburger drawer (SAP-style) — sections with expandable sub-items
const MENU_SECTIONS = [
  {
    key: 'products',
    label: 'Products',
    items: [
      { label: 'Finance',               icon: CurrencyDollar, tint: '#00B140' },
      { label: 'Procurement',           icon: ShoppingCart,   tint: '#6B7280' },
      { label: 'Invest',                icon: TrendUp,        tint: '#002677' },
      { label: 'Supply Chain',          icon: Truck,          tint: '#8347AD' },
      { label: 'Human Resources',       icon: UsersThree,     tint: '#E4002B' },
      { label: 'Customer Experience',   icon: HeartStraight,  tint: '#8347AD' },
      { label: 'Schedule & Remind',     icon: CalendarBlank,  tint: '#41B6E6', to: '/schedule', free: true },
    ],
  },
  {
    key: 'industries',
    label: 'Industries',
    items: [
      { label: 'Financial Services',    icon: BankIcon,  tint: '#002677' },
      { label: 'Manufacturing',         icon: Factory,   tint: '#6B7280' },
      { label: 'Retail & Consumer',     icon: Storefront,tint: '#FF671F' },
      { label: 'Healthcare & Life Sciences', icon: Heartbeat, tint: '#E4002B' },
      { label: 'Public Sector',         icon: Buildings, tint: '#41B6E6' },
      { label: 'Technology',            icon: Atom,      tint: '#8347AD' },
    ],
  },
  {
    key: 'transform',
    label: 'Transform and Support',
    items: [
      { label: 'Implementation Services', icon: Wrench,  tint: '#002677' },
      { label: 'Customer Support',        icon: Headset, tint: '#00B140', to: '/support' },
      { label: 'Advisory & Strategy',     icon: Lightbulb, tint: '#FFD100' },
      { label: 'Cloud Operations',        icon: Shield,  tint: '#E4002B' },
    ],
  },
  {
    key: 'learning',
    label: 'Learning',
    items: [
      { label: 'Training & Enablement',  icon: GraduationCap, tint: '#002677' },
      { label: 'Certifications',         icon: CheckCircle,   tint: '#00B140' },
      { label: 'Documentation',          icon: Folder,        tint: '#41B6E6' },
    ],
  },
  {
    key: 'community',
    label: 'Community',
    items: [
      { label: 'Forums',         icon: ChatsCircle, tint: '#002677' },
      { label: 'Events',         icon: Sparkle,     tint: '#8347AD' },
      { label: 'User Groups',    icon: UsersThree,  tint: '#00B140' },
    ],
  },
  {
    key: 'partners',
    label: 'Partners',
    items: [
      { label: 'Find a Partner',     icon: MagnifyingGlass, tint: '#002677' },
      { label: 'Become a Partner',   icon: Handshake,       tint: '#00B140' },
      { label: 'Partner Portal',     icon: Shield,          tint: '#6B7280' },
    ],
  },
];

export default function LoginPage() {
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [searchParams] = useSearchParams();
  const [openMenu, setOpenMenu] = useState(null); // which nav item dropdown is open
  const [shellOpen, setShellOpen] = useState(false); // hamburger drawer
  const [shellExpanded, setShellExpanded] = useState(null); // expanded section in drawer
  const [shellQuery, setShellQuery] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  // Convert ISO 3166-1 alpha-2 country code to a flag emoji
  const flagEmoji = (code) => {
    if (!code) return '🌐';
    return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
  };
  const availableLangs = LANGUAGES.filter((l) => SUPPORTED_LOCALES.includes(l.code));
  const currentLang = availableLangs.find((l) => l.code === language) || availableLangs[0];

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) localStorage.setItem('pending_referral_code', ref.toUpperCase());
  }, [searchParams]);

  const closeDrawer = () => {
    setShowForm(false);
    setError('');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="login-page">
      {/* === SAP-style Shell Bar === */}
      <header className="w-full bg-[#f5f6fa] dark:bg-muted/30 border-b border-border">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-3">
          {/* Square brand block (SAP logo style) */}
          <Link to="/login" className="flex items-center" data-testid="brand-block">
            <div
              className="h-14 w-16 flex items-center justify-center text-white font-heading font-bold text-xl tracking-tight"
              style={{ background: '#002677' /* Pantone Reflex Blue */ }}
            >
              BIL
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-sm hover:bg-accent/30 transition-colors"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="p-1.5 rounded-sm hover:bg-accent/30 transition-colors flex items-center gap-1"
                data-testid="language-switcher"
                aria-label="Change language"
                aria-expanded={langOpen}
              >
                <span className="text-2xl leading-none select-none" aria-hidden="true">
                  {flagEmoji(currentLang?.flag)}
                </span>
                <CaretDown size={10} weight="bold" className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setLangOpen(false)}
                    aria-hidden="true"
                  />
                  <div
                    className="absolute right-0 top-full mt-2 w-56 bg-card border border-border shadow-2xl rounded-sm z-40 overflow-hidden animate-fade-in"
                    data-testid="language-dropdown"
                  >
                    <div className="px-3 py-2 border-b border-border">
                      <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                        {t('marketing.language')}
                      </p>
                    </div>
                    <ul className="py-1 max-h-80 overflow-y-auto">
                      {availableLangs.map((lang) => {
                        const isActive = lang.code === language;
                        return (
                          <li key={lang.code}>
                            <button
                              onClick={() => { setLanguage(lang.code); setLangOpen(false); }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                                isActive
                                  ? 'bg-[#002677]/10 text-[#002677] font-semibold'
                                  : 'hover:bg-accent/40 text-foreground'
                              }`}
                              data-testid={`lang-option-${lang.code}`}
                            >
                              <span className="text-xl leading-none" aria-hidden="true">
                                {flagEmoji(lang.flag)}
                              </span>
                              <span className="flex-1">{lang.name}</span>
                              {isActive && (
                                <CheckCircle size={14} weight="fill" className="text-[#002677]" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setShellOpen(true)}
              className="p-2 rounded-sm hover:bg-accent/30 transition-colors"
              data-testid="shell-menu"
              aria-label="Menu"
            >
              <List size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* === SAP-style Shell Drawer (Hamburger) === */}
      {shellOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm animate-fade-in"
          onClick={() => setShellOpen(false)}
          data-testid="shell-drawer-backdrop"
        >
          <aside
            className="absolute right-0 top-0 bottom-0 w-full sm:w-[420px] bg-card shadow-2xl flex flex-col animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
            data-testid="shell-drawer"
          >
            {/* Top bar */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-b border-border bg-[#f5f6fa] dark:bg-muted/30">
              <button
                onClick={() => setShellOpen(false)}
                className="p-2 rounded-sm hover:bg-accent/40 transition-colors"
                data-testid="shell-drawer-close"
                aria-label="Close menu"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Search */}
            <div className="px-5 pt-5 pb-3">
              <div className="relative">
                <MagnifyingGlass
                  size={18}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#002677]"
                  weight="bold"
                />
                <input
                  type="search"
                  value={shellQuery}
                  onChange={(e) => setShellQuery(e.target.value)}
                  placeholder="Search"
                  className="w-full pl-5 pr-11 h-11 rounded-full border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#002677]/40 focus:border-[#002677] transition-all"
                  data-testid="shell-search"
                />
              </div>
            </div>

            {/* Sections list */}
            <nav className="flex-1 overflow-y-auto py-2" data-testid="shell-sections">
              {MENU_SECTIONS
                .filter((s) => !shellQuery ||
                  s.label.toLowerCase().includes(shellQuery.toLowerCase()) ||
                  s.items.some((i) => i.label.toLowerCase().includes(shellQuery.toLowerCase()))
                )
                .map((section) => {
                  const expanded = shellExpanded === section.key;
                  const visibleItems = shellQuery
                    ? section.items.filter((i) => i.label.toLowerCase().includes(shellQuery.toLowerCase()))
                    : section.items;
                  return (
                    <div key={section.key} className="border-b border-border/80">
                      <button
                        onClick={() => setShellExpanded(expanded ? null : section.key)}
                        className="w-full flex items-center justify-between px-5 py-5 text-left hover:bg-accent/30 transition-colors"
                        data-testid={`shell-section-${section.key}`}
                        aria-expanded={expanded}
                      >
                        <span className="font-heading text-lg font-medium text-foreground">
                          {section.label}
                        </span>
                        <CaretRight
                          size={16}
                          weight="bold"
                          className={`text-[#002677] transition-transform ${expanded ? 'rotate-90' : ''}`}
                        />
                      </button>
                      {(expanded || shellQuery) && (
                        <div className="pb-3 bg-accent/10">
                          {visibleItems.map((item) => {
                            const Icon = item.icon;
                            return (
                              <button
                                key={item.label}
                                onClick={() => {
                                  setShellOpen(false);
                                  if (item.to) navigate(item.to);
                                  else setShowForm(true);
                                }}
                                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-accent/40 transition-colors group"
                                data-testid={`shell-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <div
                                  className="h-8 w-8 rounded-sm flex items-center justify-center flex-shrink-0"
                                  style={{ background: `${item.tint}1A` }}
                                >
                                  <Icon size={16} weight="duotone" color={item.tint} />
                                </div>
                                <span className="text-sm font-medium text-foreground flex-1">
                                  {item.label}
                                </span>
                                {item.free && (
                                  <span
                                    className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm text-white mr-1"
                                    style={{ background: '#00B140' }}
                                  >
                                    Free
                                  </span>
                                )}
                                <CaretRight
                                  size={12}
                                  weight="bold"
                                  className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                                />
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              }
            </nav>

            {/* Footer CTA */}
            <div className="p-5 border-t border-border bg-[#f5f6fa] dark:bg-muted/30 flex flex-col gap-2">
              <Button
                onClick={() => { setShellOpen(false); setShowForm(true); }}
                className="w-full rounded-sm font-medium h-11"
                style={{ background: '#002677' }}
                data-testid="shell-drawer-signin"
              >
                {t('marketing.signIn')} <ArrowRight size={16} className="ml-2" weight="bold" />
              </Button>
              <Link
                to="/register"
                onClick={() => setShellOpen(false)}
                className="w-full h-11 flex items-center justify-center rounded-sm text-sm font-medium border-2 border-foreground/80 hover:bg-foreground hover:text-background transition-colors"
                data-testid="shell-drawer-register"
              >
                {t('marketing.createAccount')}
              </Link>
            </div>
          </aside>
        </div>
      )}

      {/* === Breadcrumb === */}
      <div className="max-w-[1400px] w-full mx-auto px-6 pt-8">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <CaretRight size={12} />
          <span>{t('marketing.allProducts')}</span>
        </div>
      </div>

      {/* === Category + horizontal nav (underline style) === */}
      <div
        className="max-w-[1400px] w-full mx-auto px-6 mt-5 border-b border-border relative"
        onMouseLeave={() => setOpenMenu(null)}
      >
        <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-10">
          <h2 className="font-heading text-xl lg:text-2xl font-semibold whitespace-nowrap">
            Business Intelligence Lounge
          </h2>
          <nav className="flex items-center gap-7 overflow-x-visible" data-testid="category-nav">
            {NAV_ITEMS.map((item, i) => {
              const isActive = i === 0;
              const isOpen = openMenu === item.key;
              const navKey = item.key === 'use-cases' ? 'useCases' : item.key;
              const label = t(`marketing.nav.${navKey}`) || item.label;
              return (
                <div
                  key={item.key}
                  className="relative"
                  onMouseEnter={() => item.hasDropdown && setOpenMenu(item.key)}
                >
                  <button
                    onClick={() => {
                      if (item.hasDropdown) {
                        setOpenMenu(isOpen ? null : item.key);
                        return;
                      }
                      setOpenMenu(null);
                      if (item.key === 'pricing')        navigate('/pricing');
                      else if (item.key === 'use-cases') navigate('/use-cases');
                      else if (item.key === 'partners')  navigate('/partners');
                      else if (item.key === 'resources') navigate('/resources');
                      else if (item.key === 'support')   navigate('/support');
                    }}
                    className={`relative pb-3 text-sm whitespace-nowrap transition-colors flex items-center gap-1 ${
                      isActive || isOpen
                        ? 'font-semibold text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`nav-${item.key}`}
                    aria-expanded={item.hasDropdown ? isOpen : undefined}
                  >
                    {label}
                    {item.hasDropdown && (
                      <CaretDown
                        size={12}
                        weight="bold"
                        className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    )}
                    {(isActive || isOpen) && (
                      <span
                        className="absolute left-0 right-0 -bottom-px h-[3px]"
                        style={{ background: '#002677' }}
                      />
                    )}
                  </button>
                </div>
              );
            })}
          </nav>
        </div>

        {/* === Mega Menus (Products / Solutions) === */}
        {(openMenu === 'products' || openMenu === 'solutions') && (() => {
          const isProducts = openMenu === 'products';
          const items = isProducts ? PRODUCTS : SOLUTIONS;
          const ns = isProducts ? 'productsMenu' : 'solutionsMenu';
          const heading = t(`marketing.${ns}.title`);
          const blurb = t(`marketing.${ns}.blurb`);
          const eyebrow = isProducts ? t('marketing.nav.products') : t('marketing.nav.solutions');
          const ctaLabel = t(`marketing.${ns}.exploreAll`);
          const prefix = isProducts ? 'product' : 'solution';
          return (
            <div
              className="absolute left-0 right-0 top-full z-40 bg-card border border-border border-t-0 shadow-2xl animate-fade-in"
              data-testid={`${prefix}s-mega-menu`}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <div className="max-w-[1400px] mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                <div className="lg:col-span-1 pr-6 border-r border-border">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                    {eyebrow}
                  </p>
                  <h3 className="font-heading text-2xl font-semibold mt-2 leading-tight">
                    {heading}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                    {blurb}
                  </p>
                  <button
                    onClick={() => setShowForm(true)}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#002677] hover:gap-3 transition-all"
                    data-testid={`${prefix}s-mega-menu-cta`}
                  >
                    {ctaLabel} <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-1 pl-0 lg:pl-6">
                  {items.map((p) => {
                    const Icon = p.icon;
                    // Map item key to i18n camelCase key
                    const itemKey = p.key === 'supply-chain' ? 'supplyChain' : p.key;
                    const itemLabel = t(`marketing.${ns}.${itemKey}`) || p.label;
                    const itemDesc = t(`marketing.${ns}.${itemKey}Desc`) || p.desc;
                    return (
                      <button
                        key={p.key}
                        onClick={() => {
                          if (p.key === 'schedule') {
                            navigate('/schedule');
                          } else {
                            setShowForm(true);
                          }
                        }}
                        className="group flex items-start gap-3 p-4 rounded-sm text-left hover:bg-accent/40 transition-colors"
                        data-testid={`${prefix}-${p.key}`}
                      >
                        <div
                          className="h-10 w-10 rounded-sm flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                          style={{ background: `${p.tint}1A` }}
                        >
                          <Icon size={20} weight="duotone" color={p.tint} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                            {itemLabel}
                            {p.free && (
                              <span
                                className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm text-white"
                                style={{ background: '#00B140' }}
                              >
                                Free
                              </span>
                            )}
                            <CaretRight
                              size={11}
                              weight="bold"
                              className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                              style={{ color: p.tint }}
                            />
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 leading-snug">
                            {itemDesc}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* === HERO === */}
      <section
        className="flex-1 relative overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, #eef2ff 0%, #e6efff 40%, #dae8fb 100%)',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left column — hero copy + CTAs */}
          <div className="max-w-xl relative z-10">
            <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0a0a0a] dark:text-foreground">
              {t('marketing.hero')}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t('marketing.heroSubtitle')}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="hero-ctas">
              <Button
                size="lg"
                onClick={() => setShowForm(true)}
                className="rounded-sm font-medium h-12 px-7 text-base"
                style={{ background: '#002677' }}
                data-testid="cta-sign-in"
              >
                {t('marketing.signIn')}
                <ArrowRight className="ml-2" size={18} weight="bold" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                asChild
                className="rounded-sm font-medium h-12 px-7 text-base border-2 border-foreground/80 bg-transparent hover:bg-foreground hover:text-background"
              >
                <Link to="/register" data-testid="cta-create-account">{t('marketing.createAccount')}</Link>
              </Button>
            </div>

            <Link
              to="/pricing"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#002677] hover:gap-2.5 transition-all"
              data-testid="cta-see-pricing"
            >
              {t('marketing.seePricing')} <ArrowRight size={14} weight="bold" />
            </Link>

            {/* Trust strip */}
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs tracking-[0.18em] uppercase text-muted-foreground">
              <span>{t('marketing.chips.dataRooms')}</span>
              <span>·</span>
              <span>{t('marketing.chips.aiDealScoring')}</span>
              <span>·</span>
              <span>{t('marketing.chips.procurement')}</span>
              <span>·</span>
              <span>{t('marketing.chips.compliance')}</span>
            </div>
          </div>

          {/* Right column — floating feature illustration */}
          <div className="relative hidden lg:block h-[520px]">
            {/* Central AI hub */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-64 w-64 rounded-sm bg-white shadow-2xl flex flex-col items-center justify-center gap-3 z-20"
              style={{ boxShadow: '0 24px 48px -12px rgba(0, 38, 119, 0.25)' }}
            >
              <div
                className="h-16 w-16 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #002677 0%, #41B6E6 100%)' }}
              >
                <Sparkle size={32} weight="fill" className="text-white" />
              </div>
              <div className="text-center">
                <p className="font-heading font-bold text-lg tracking-tight">Business AI</p>
                <p className="text-xs text-muted-foreground mt-1 tracking-wider uppercase">
                  Unified copilot
                </p>
              </div>
              {/* decorative binary strip */}
              <div className="absolute inset-x-6 bottom-6 text-[10px] font-mono text-muted-foreground/40 select-none leading-tight overflow-hidden h-6">
                01011001 10110 10010 1 0110<br />01 11010 1010110 0010 11
              </div>
            </div>

            {/* Orbit sparkles */}
            <Sparkle size={22} weight="fill" className="absolute left-[42%] top-[15%] text-[#002677] z-10" />
            <Sparkle size={16} weight="fill" className="absolute left-[30%] top-[40%] text-[#41B6E6] z-10" />
            <Sparkle size={18} weight="fill" className="absolute right-[10%] top-[22%] text-[#8347AD] z-10" />
            <Sparkle size={14} weight="fill" className="absolute right-[28%] bottom-[18%] text-[#002677]/70 z-10" />

            {/* Floating feature cards */}
            <FloatCard
              position="top-[6%] left-[2%]"
              icon={Handshake}
              title="AI Deal Readiness"
              tint="#002677"
              delay="0s"
            />
            <FloatCard
              position="top-[14%] right-[4%]"
              icon={Folder}
              title="Data Rooms"
              tint="#41B6E6"
              delay="0.5s"
            />
            <FloatCard
              position="bottom-[18%] left-[4%]"
              icon={Lightning}
              title="Workflow AI"
              tint="#E4002B"
              delay="1s"
            />
            <FloatCard
              position="bottom-[4%] right-[8%]"
              icon={ChartLineUp}
              title="Pipeline Forecast"
              tint="#00B140"
              delay="1.5s"
            />
            <FloatCard
              position="top-[46%] right-[0%]"
              icon={Shield}
              title="GRC & Audit"
              tint="#FFD100"
              delay="2s"
              darkText
            />

            {/* Dotted connection lines */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern id="dots" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                  <circle cx="1.5" cy="1.5" r="1" fill="#8347AD" opacity="0.35" />
                </pattern>
              </defs>
              <path d="M 80 80 Q 250 180 300 260" stroke="url(#grad1)" strokeWidth="1.5" strokeDasharray="4 4" fill="none" opacity="0.45" />
              <path d="M 500 80 Q 380 180 340 260" stroke="url(#grad1)" strokeWidth="1.5" strokeDasharray="4 4" fill="none" opacity="0.45" />
              <path d="M 80 430 Q 220 360 300 320" stroke="url(#grad1)" strokeWidth="1.5" strokeDasharray="4 4" fill="none" opacity="0.45" />
              <path d="M 500 470 Q 380 380 340 320" stroke="url(#grad1)" strokeWidth="1.5" strokeDasharray="4 4" fill="none" opacity="0.45" />
              <linearGradient id="grad1" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#002677" />
                <stop offset="100%" stopColor="#41B6E6" />
              </linearGradient>
            </svg>
          </div>
        </div>
      </section>

      {/* === Auth dialog (extracted to components/AuthDialog.js) === */}
      <AuthDialog open={showForm} onClose={closeDrawer} />
    </div>
  );
}

function FloatCard({ position, icon: Icon, title, tint, delay = '0s', darkText = false }) {
  return (
    <div
      className={`absolute ${position} bg-white rounded-sm shadow-xl px-4 py-3 flex items-center gap-3 z-10 animate-float`}
      style={{ animationDelay: delay, boxShadow: '0 12px 28px -8px rgba(0, 38, 119, 0.18)' }}
    >
      <div
        className="h-9 w-9 rounded-sm flex items-center justify-center flex-shrink-0"
        style={{ background: `${tint}1A` /* ~10% opacity */ }}
      >
        <Icon size={18} weight="duotone" color={darkText ? '#1a1a1a' : tint} />
      </div>
      <span className="text-sm font-medium text-[#0a0a0a] whitespace-nowrap">{title}</span>
    </div>
  );
}
